import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

type UsePanelResizeOptions = {
  getWidth: (clientX: number) => number;
  onResize: (width: number) => void;
};

type UsePanelResizeResult = {
  isResizing: boolean;
  startResize: (event: ReactPointerEvent<HTMLElement>) => void;
};

export function usePanelResize({
  getWidth,
  onResize,
}: UsePanelResizeOptions): UsePanelResizeResult {
  const getWidthRef = useRef(getWidth);
  const onResizeRef = useRef(onResize);
  const stopResizeRef = useRef<(() => void) | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  getWidthRef.current = getWidth;
  onResizeRef.current = onResize;

  useEffect(() => () => stopResizeRef.current?.(), []);

  const startResize = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    stopResizeRef.current?.();

    const captureTarget = event.currentTarget;
    const pointerId = event.pointerId;

    setIsResizing(true);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    captureTarget.setPointerCapture(pointerId);

    const cleanupResize = () => {
      setIsResizing(false);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      captureTarget.removeEventListener('pointermove', handleMove);
      captureTarget.removeEventListener('pointerup', handlePointerEnd);
      captureTarget.removeEventListener('pointercancel', handlePointerEnd);
      captureTarget.removeEventListener('lostpointercapture', handlePointerEnd);

      try {
        if (captureTarget.hasPointerCapture(pointerId)) {
          captureTarget.releasePointerCapture(pointerId);
        }
      } catch {
        // Pointer capture may already be released.
      }

      stopResizeRef.current = null;
    };

    const handleMove = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) {
        return;
      }
      onResizeRef.current(getWidthRef.current(moveEvent.clientX));
    };

    const handlePointerEnd = (endEvent: PointerEvent) => {
      if (endEvent.pointerId === pointerId) {
        cleanupResize();
      }
    };

    stopResizeRef.current = cleanupResize;
    captureTarget.addEventListener('pointermove', handleMove);
    captureTarget.addEventListener('pointerup', handlePointerEnd);
    captureTarget.addEventListener('pointercancel', handlePointerEnd);
    captureTarget.addEventListener('lostpointercapture', handlePointerEnd);
  }, []);

  return { isResizing, startResize };
}
